import { useState } from 'react'
import {
	type PlaceSuggestion,
	usePlaceSuggestions
} from '#hooks/use-place-suggestions'
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem,
	ComboboxList
} from '#ui/combobox'

interface PickedPlace {
	placeId: string
	latitude: number
	longitude: number
	formattedAddress: string
}

/**
 * The address search of Google Places (the Places API New), limited to Italy, in the same field as
 * the addresses of the forms. Picking a suggestion closes the search session with one request for
 * the details of the place, and asks only for the fields of the Essentials tier: id, location,
 * formatted address and its components (decision D30). The coordinates come to the browser and the
 * page saves them: no server of ours calls Google.
 */
function PlaceSearch({
	apiKey,
	language,
	onPick,
	attribution,
	failedMessage,
	id,
	label
}: {
	apiKey: string
	language: string
	onPick: (place: PickedPlace) => void
	/** The line that credits Google under the suggestions ("Suggerimenti di Google"). */
	attribution: string
	/** Shown under the field when Google cannot be reached. */
	failedMessage: string
	id?: string
	/** The name of the field for assistive technology, when the page has no visible label tied to `id`. */
	label?: string
}) {
	const [text, setText] = useState('')
	const [open, setOpen] = useState(false)
	const { suggestions, failed, ask, close, endSession } = usePlaceSuggestions(
		{ apiKey, language }
	)

	const pick = async (item: PlaceSuggestion | null) => {
		if (!item) {
			return
		}
		close()
		setOpen(false)
		setText(item.label)
		try {
			const place = item.prediction.toPlace()
			await place.fetchFields({
				fields: [
					'id',
					'location',
					'formattedAddress',
					'addressComponents'
				]
			})
			if (place.id && place.location && place.formattedAddress) {
				onPick({
					placeId: place.id,
					latitude: place.location.lat(),
					longitude: place.location.lng(),
					formattedAddress: place.formattedAddress
				})
			}
		} finally {
			endSession()
		}
	}

	return (
		<div className='flex flex-col gap-2'>
			<Combobox<PlaceSuggestion>
				filter={null}
				inputValue={text}
				isItemEqualToValue={(a, b) => a.value === b.value}
				items={suggestions}
				itemToStringLabel={(item) => item.label}
				itemToStringValue={(item) => item.value}
				onInputValueChange={(next, details) => {
					if (details.reason === 'input-change') {
						setText(next)
						setOpen(true)
						ask(next)
					}
				}}
				onOpenChange={setOpen}
				onValueChange={pick}
				open={open && suggestions.length > 0}
				value={null}
			>
				<ComboboxInput
					aria-label={label}
					autoComplete='off'
					id={id}
					showTrigger={false}
				/>
				<ComboboxContent>
					<ComboboxList>
						{(item: PlaceSuggestion) => (
							<ComboboxItem key={item.value} value={item}>
								{item.label}
							</ComboboxItem>
						)}
					</ComboboxList>
					<p className='border-border border-t px-3 py-2 text-muted-foreground text-xs'>
						{attribution}
					</p>
				</ComboboxContent>
			</Combobox>
			{failed && (
				<p className='text-destructive text-sm'>{failedMessage}</p>
			)}
		</div>
	)
}

export { PlaceSearch }
export type { PickedPlace }
